import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, HardHat, Search, Upload, X, PhoneCall } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { formatMoney } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusChip, statusTone } from "@/components/common/StatusChip";
import { semaforoTone } from "@/lib/wo";
import { SubDialog } from "@/features/subs/SubDialog";
import { SubImportDialog } from "@/features/subs/SubImportDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 50;
const CHANNEL_TONE = { whatsapp: "green", sms: "blue", voz: "amber" };

export default function SubsPage() {
  const { t } = useT();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [dialog, setDialog] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const get = (k, d = "") => params.get(k) || d;
  const patch = (changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => {
      if (!v || v === "all") next.delete(k);
      else next.set(k, v);
    });
    if (!("page" in changes)) next.delete("page");
    setParams(next, { replace: true });
  };

  const page = Number(get("page", "1"));
  const filters = {
    q: get("q") || undefined,
    especialidad: get("especialidad") || undefined,
    canal: get("canal") || undefined,
    modalidad: get("modalidad") || undefined,
    status: get("status") || "active",
  };

  const { data, isLoading } = useQuery({
    queryKey: ["subs", { ...filters, page }],
    queryFn: async () =>
      (await api.get("/subs", { params: { ...filters, page, page_size: PAGE_SIZE } })).data,
  });

  const { data: serviceTypes } = useQuery({
    queryKey: ["service-types"],
    queryFn: async () => (await api.get("/service-types")).data,
  });

  const rows = data?.items || [];
  const showRate = rows.some((r) => r.tarifa_promedio_cents !== undefined);
  const showPago = rows.some((r) => r.pago_semaforo !== undefined);

  const columns = [
    {
      key: "nombre",
      label: t("app.name"),
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate text-[13px] font-medium text-ink">{r.nombre}</p>
          <p className="truncate text-[11.5px] text-ink-tertiary">{r.empresa || r.email || "—"}</p>
        </div>
      ),
    },
    {
      key: "especialidades_nombres",
      label: t("subs.specialties"),
      render: (r) => (
        <span className="text-[12.5px] text-ink-secondary">
          {(r.especialidades_nombres || []).slice(0, 3).join(", ") || "—"}
        </span>
      ),
    },
    {
      key: "telefono",
      label: t("app.phone"),
      width: 140,
      render: (r) => <span className="tabular-nums">{r.telefono}</span>,
    },
    {
      key: "canal_preferido",
      label: t("subs.channel"),
      width: 140,
      render: (r) => (
        <span className="inline-flex items-center gap-1.5">
          <StatusChip tone={CHANNEL_TONE[r.canal_preferido] || "gray"}
            label={t(`channels.${r.canal_preferido}`)} testId={`sub-channel-${r.id}`} />
          {r.sabe_leer === false && (
            <span title={t("subs.voice_forced")} data-testid={`sub-voice-flag-${r.id}`}
              className="inline-flex h-[18px] items-center gap-1 rounded-[5px] bg-state-warn-soft px-1.5 text-[10.5px] font-semibold text-state-warn ring-1 ring-state-warn-soft">
              <PhoneCall className="h-2.5 w-2.5" /> {t("subs.no_read_short")}
            </span>
          )}
        </span>
      ),
    },
    { key: "activas", label: t("subs.active_orders"), width: 96, align: "right" },
    { key: "completadas_30d", label: t("subs.done_30"), width: 110, align: "right" },
    {
      key: "modalidad_liquidacion",
      label: t("subs.settlement"),
      width: 110,
      render: (r) => t(`subs.settlements.${r.modalidad_liquidacion || "quincenal"}`),
    },
    ...(showRate
      ? [{
          key: "tarifa_promedio_cents", label: t("subs.rate"), width: 110, align: "right",
          render: (r) => formatMoney(r.tarifa_promedio_cents),
        }]
      : []),
    {
      key: "status",
      label: t("app.status"),
      width: 100,
      render: (r) => <StatusChip tone={statusTone(r.status)} label={t(`status.${r.status}`)} />,
    },
    ...(showPago
      ? [{
          key: "pago_semaforo", label: t("wo.pago_sub"), width: 130,
          render: (r) => r.pago_semaforo ? (
            <StatusChip testId={`sub-pago-semaforo-${r.id}`} tone={semaforoTone(r.pago_semaforo)}
              label={t(`payment_semaforo.${r.pago_semaforo}`)} />
          ) : <span className="text-ink-tertiary">—</span>,
        }]
      : []),
  ];

  const activeFilters = ["q", "especialidad", "canal", "modalidad", "status"].filter((k) => params.get(k));

  return (
    <>
      <PageHeader
        title={t("subs.title")}
        subtitle={t("subs.subtitle")}
        testId="subs-header"
        actions={
          can("subs", "create") && (
            <>
              <Button variant="outline" data-testid="subs-import-button" onClick={() => setImportOpen(true)}
                className="h-8 rounded-[6px] text-[13px]">
                <Upload className="mr-1.5 h-3.5 w-3.5" /> {t("subs.import.button")}
              </Button>
              <Button data-testid="new-sub-button" onClick={() => setDialog(true)}
                className="h-8 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover">
                <Plus className="mr-1.5 h-3.5 w-3.5" /> {t("subs.new")}
              </Button>
            </>
          )
        }
      />

      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-white px-6 py-2.5">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-tertiary" />
          <Input
            data-testid="subs-search-input"
            defaultValue={get("q")}
            onKeyDown={(e) => e.key === "Enter" && patch({ q: e.currentTarget.value })}
            onBlur={(e) => patch({ q: e.currentTarget.value })}
            placeholder={t("subs.search")}
            className="h-8 w-[280px] rounded-[6px] pl-8 text-[13px]"
          />
        </div>
        <Select value={get("especialidad", "all")} onValueChange={(v) => patch({ especialidad: v })}>
          <SelectTrigger data-testid="subs-specialty-filter" className="h-8 w-[200px] rounded-[6px] text-[13px]">
            <SelectValue placeholder={t("subs.specialties")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("subs.specialties")}: {t("app.all")}</SelectItem>
            {(serviceTypes?.items || []).map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={get("canal", "all")} onValueChange={(v) => patch({ canal: v })}>
          <SelectTrigger data-testid="subs-channel-filter" className="h-8 w-[150px] rounded-[6px] text-[13px]">
            <SelectValue placeholder={t("subs.channel")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("subs.channel")}: {t("app.all")}</SelectItem>
            {["whatsapp", "sms", "voz"].map((c) => (
              <SelectItem key={c} value={c}>{t(`channels.${c}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={get("modalidad", "all")} onValueChange={(v) => patch({ modalidad: v })}>
          <SelectTrigger data-testid="subs-settlement-filter" className="h-8 w-[160px] rounded-[6px] text-[13px]">
            <SelectValue placeholder={t("subs.settlement")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("subs.settlement")}: {t("app.all")}</SelectItem>
            {["semanal", "quincenal"].map((m) => (
              <SelectItem key={m} value={m}>{t(`subs.settlements.${m}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={get("status", "active")} onValueChange={(v) => patch({ status: v })}>
          <SelectTrigger data-testid="subs-status-filter" className="h-8 w-[140px] rounded-[6px] text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">{t("status.active")}</SelectItem>
            <SelectItem value="inactive">{t("status.inactive")}</SelectItem>
            <SelectItem value="all">{t("app.all")}</SelectItem>
          </SelectContent>
        </Select>
        {!!activeFilters.length && (
          <Button variant="ghost" data-testid="subs-clear-filters"
            onClick={() => setParams(new URLSearchParams(), { replace: true })}
            className="h-8 rounded-[6px] text-[12.5px] text-ink-tertiary">
            <X className="mr-1 h-3.5 w-3.5" /> {t("wo.clear_filters")}
          </Button>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col bg-white" data-testid="subs-page">
        <DataTable
          testId="subs-table"
          columns={columns}
          rows={rows}
          total={data?.total || 0}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={(p) => patch({ page: String(p) })}
          loading={isLoading}
          onRowClick={(r) => navigate(`/subcontratistas/${r.id}`)}
          empty={
            <EmptyState
              icon={HardHat}
              testId="subs-empty"
              title={t("subs.empty_title")}
              description={t("subs.empty_desc")}
              action={
                can("subs", "create") && (
                  <Button data-testid="subs-empty-new" onClick={() => setDialog(true)}
                    className="h-8 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover">
                    {t("subs.new")}
                  </Button>
                )
              }
            />
          }
        />
      </div>

      <SubDialog open={dialog} onOpenChange={setDialog} />
      <SubImportDialog open={importOpen} onOpenChange={setImportOpen} />
    </>
  );
}
